import React from "react"
import {
    Box,
    Text,
    HorizontalStack,
    componentStyles,
    ExternalLink,
    useColorModeValue,
    Tooltip,
} from "@mallardbay/lib-react-components"
import { Link } from "gatsby"
import type { IconProp } from "@fortawesome/fontawesome-svg-core"
import { useMatch } from "@reach/router"

import { useIsMobile } from "~utils/hooks/use-is-mobile"
import type { FeatureStatusValue } from "~components/shared/feature-status-label"
import FeatureStatusLabel from "~components/shared/feature-status-label"
import { TEST_IDS } from "~config/constants"

import { useSharedStyles } from "./sidebar-styles"
import SidebarIcon from "./sidebar-icon"
import SidebarFeatureLockIndicator from "./sidebar-feature-lock-indicator"

interface SidebarRowProps {
    readonly label: string
    readonly link: string
    readonly icon?: IconProp
    readonly isSidebarCollapsed: boolean
    readonly isFeatureBlocked?: boolean
    readonly bgColor: string
    readonly onClick: () => void
    readonly rightContent?: React.ReactNode // example: Arrow for main item
    readonly paddingLeft?: string // for subitems to indent
    readonly isExternalLink?: boolean
    readonly featureStatus?: FeatureStatusValue | null
}

export default function SidebarRow({
    label,
    link,
    icon,
    isSidebarCollapsed,
    isFeatureBlocked,
    bgColor,
    onClick,
    rightContent,
    paddingLeft,
    isExternalLink,
    featureStatus,
}: SidebarRowProps) {
    const isActiveItem = Boolean(useMatch(link === "/" ? link : `${link}/*`))
    const sharedStyles = useSharedStyles({ isActiveItem })
    const styles = useStyles({
        paddingLeft,
        isActiveItem,
        bgColor,
        isSidebarCollapsed,
    })
    const isMobile = useIsMobile()

    const LinkComponent = isExternalLink ? ExternalLink : Link

    const sidebarContent = (
        <LinkComponent
            to={link}
            href={link}
            onClick={onClick}
            style={styles.link}
        >
            <Box style={styles.container}>
                <HorizontalStack style={styles.horizontalStack}>
                    {isActiveItem && (
                        <Box
                            style={sharedStyles.activeBar}
                            data-testid={TEST_IDS.SIDEBAR_ACTIVE_BAR}
                        />
                    )}
                    <SidebarIcon
                        icon={icon}
                        isActiveItem={isActiveItem}
                        isFeatureBlocked={isFeatureBlocked}
                        isSidebarCollapsed={isSidebarCollapsed}
                    />
                    {!isSidebarCollapsed && (
                        <Text style={sharedStyles.label}>{label}</Text>
                    )}
                </HorizontalStack>
                {!isSidebarCollapsed && (
                    <HorizontalStack style={styles.rightContentContainer}>
                        <FeatureStatusLabel featureStatus={featureStatus} />
                        {rightContent}
                    </HorizontalStack>
                )}
                {isFeatureBlocked && !isSidebarCollapsed && (
                    <SidebarFeatureLockIndicator />
                )}
            </Box>
        </LinkComponent>
    )

    // Show tooltip only when sidebar is collapsed and on desktop
    const shouldShowTooltip = isSidebarCollapsed && !isMobile

    if (shouldShowTooltip) {
        return (
            <Tooltip label={label} placement="right">
                {sidebarContent}
            </Tooltip>
        )
    }

    return sidebarContent
}

function useStyles({
    isActiveItem,
    bgColor,
    isSidebarCollapsed,
    paddingLeft,
}: {
    isActiveItem: boolean
    bgColor: string
    isSidebarCollapsed: boolean
    paddingLeft?: string
}) {
    const hoverBgColor = useColorModeValue("gray.100", "gray.800")

    return componentStyles({
        container: {
            flex: "1",
            textAlign: "left",
            paddingY: 2,
            paddingRight: 3,
            paddingLeft: 2,
            width: "100%",
            display: "flex",
            position: "relative",
            alignItems: "center",
            justifyContent: isSidebarCollapsed ? "center" : "space-between",
            backgroundColor: isActiveItem ? bgColor : "initial",
            transition: "all .2s",
            ...(paddingLeft && { paddingLeft }),
            _hover: {
                backgroundColor: hoverBgColor,
            },
        },
        horizontalStack: {
            gap: 0,
            justifyContent: "space-between",
        },
        labelContainer: {
            gap: 1,
            alignItems: "center",
            width: "100%",
        },
        rightContentContainer: {
            gap: 1,
            alignItems: "center",
        },
        link: {
            width: "100%",
        },
    })
}
